/**
 * FileUploadTab Component
 *
 * Multi-file upload surface of the create dialog: a drop zone (native HTML5
 * drag and drop, no extra dependency), a hidden but focusable file input for
 * keyboard users, and the queue list with per-file status and progress.
 *
 * Drag state uses an enter/leave counter instead of relatedTarget checks:
 * dragleave fires for every child element the pointer crosses and Firefox
 * reports relatedTarget as null, so a counter is the only reliable signal.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import { FileUploadTabProps } from '../types';
import { useUploadCapabilities } from '../../../../hooks/documents/useUploadCapabilities';
import {
  MAX_TITLE_LENGTH,
  UploadItem,
  UploadItemStatus,
  filesFromClipboard,
  filesFromDataTransfer,
} from '../../../../hooks/documents/uploadQueue';

const INPUT_ID = 'document-upload-input';

// Human-readable format label for an extension (for the "supported formats" hint).
const EXTENSION_LABELS: Record<string, string> = {
  '.pdf': 'PDF',
  '.docx': 'DOCX',
  '.pptx': 'PPTX',
  '.xlsx': 'XLSX',
  '.txt': 'TXT',
  '.png': 'PNG',
  '.jpg': 'JPG',
  '.jpeg': 'JPEG',
};

function formatList(extensions: string[]): string {
  // De-duplicate labels (so .jpg/.jpeg do not both show) while preserving order.
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const ext of extensions) {
    const label = EXTENSION_LABELS[ext] ?? ext.replace('.', '').toUpperCase();
    if (!seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  return labels.join(', ');
}

function formatSize(bytes: number): string {
  if (bytes >= 1048576) {
    return `${(bytes / 1048576).toFixed(1)} MB`;
  }
  return `${(bytes / 1024).toFixed(1)} KB`;
}

type ChipColor = 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info';

function statusColor(status: UploadItemStatus): ChipColor {
  switch (status) {
    case 'done':
      return 'success';
    case 'error':
      return 'error';
    case 'uploading':
      return 'primary';
    case 'duplicate':
      return 'warning';
    case 'skipped':
      return 'info';
    default:
      return 'default';
  }
}

interface QueueRowProps {
  item: UploadItem;
  onRemove: (id: string) => void;
  onTitleChange: (id: string, title: string) => void;
  removable: boolean;
  /** True while several files are queued: each row then carries its own title field. */
  editableTitle: boolean;
}

const QueueRow: React.FC<QueueRowProps> = ({ item, onRemove, onTitleChange, removable, editableTitle }) => {
  const { t } = useTranslation();
  const errorId = `upload-error-${item.id}`;
  const canEdit = editableTitle && (item.status === 'pending' || item.status === 'error');
  const secondary = item.error ?? formatSize(item.file.size);

  return (
    <ListItem
      divider
      aria-describedby={item.error ? errorId : undefined}
      secondaryAction={
        removable ? (
          <IconButton
            edge="end"
            size="small"
            aria-label={t('documents.upload.removeFile')}
            onClick={() => onRemove(item.id)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : null
      }
    >
      <ListItemIcon sx={{ minWidth: 36 }}>
        <InsertDriveFileIcon fontSize="small" />
      </ListItemIcon>
      {canEdit ? (
        <Box sx={{ flex: '1 1 auto', minWidth: 0, pr: 1, py: 1 }}>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            label={t('documents.upload.titlePlaceholder')}
            value={item.title}
            onChange={(e) => onTitleChange(item.id, e.target.value)}
            slotProps={{
              htmlInput: {
                maxLength: MAX_TITLE_LENGTH,
                'aria-label': t('documents.upload.titleFor', { file: item.file.name }),
              },
            }}
          />
          <Typography
            id={errorId}
            variant="caption"
            color={item.error ? 'error' : 'text.secondary'}
            noWrap
            title={item.file.name}
            sx={{ display: 'block', mt: 0.5, pl: 0.25 }}
          >
            {item.file.name} · {secondary}
          </Typography>
        </Box>
      ) : (
        <ListItemText
          primary={editableTitle ? item.title || item.file.name : item.file.name}
          secondary={editableTitle && item.title ? `${item.file.name} · ${secondary}` : secondary}
          slotProps={{
            primary: { noWrap: true, title: item.file.name },
            secondary: {
              id: errorId,
              noWrap: true,
              color: item.error ? 'error' : 'text.secondary',
            },
          }}
          sx={{ pr: 1, minWidth: 0 }}
        />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, mr: removable ? 4 : 0 }}>
        {item.status === 'uploading' && (
          <LinearProgress
            variant="determinate"
            value={item.progress}
            aria-label={t('documents.upload.uploading')}
            sx={{ width: 96 }}
          />
        )}
        <Chip
          size="small"
          label={t(`documents.upload.status.${item.status}`)}
          color={statusColor(item.status)}
          variant={item.status === 'pending' ? 'outlined' : 'filled'}
        />
      </Box>
    </ListItem>
  );
};

const FileUploadTabInner: React.FC<FileUploadTabProps> = ({
  items,
  onFilesAdded,
  onRemove,
  onTitleChange,
  onFileSelect,
  fileInputRef,
  uploading,
  maxFileSizeMB,
  summary,
}) => {
  const { t } = useTranslation();
  const { capabilities } = useUploadCapabilities();
  const [dragDepth, setDragDepth] = useState(0);
  const dragActive = dragDepth > 0;

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragDepth((depth) => depth + 1);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragDepth((depth) => Math.max(0, depth - 1));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragDepth(0);
      if (uploading) {
        return;
      }
      // Folders are walked asynchronously; the entries are captured before
      // the handler returns, so the cleared DataTransfer is not a problem.
      void filesFromDataTransfer(e.dataTransfer).then((files) => {
        if (files.length > 0) {
          onFilesAdded(files);
        }
      });
    },
    [onFilesAdded, uploading]
  );

  // Paste-to-upload: a screenshot or a file copied in the OS explorer lands
  // in the queue when pasted anywhere while this tab is shown. Text pastes
  // (no files) are left alone so typing in the title field keeps working.
  useEffect(() => {
    if (uploading) {
      return undefined;
    }
    const onPaste = (event: ClipboardEvent) => {
      const files = filesFromClipboard(event.clipboardData);
      if (files.length > 0) {
        event.preventDefault();
        onFilesAdded(files);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [onFilesAdded, uploading]);

  const finished = summary.done + summary.failed + summary.skipped;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* The label makes the whole zone a click target for the file input;
          the input itself stays focusable (visually hidden, not display:none)
          so Tab + Enter/Space opens the native picker. */}
      <Box
        component="label"
        htmlFor={INPUT_ID}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          display: 'block',
          position: 'relative',
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'divider',
          bgcolor: dragActive ? 'action.hover' : 'transparent',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          opacity: uploading ? 0.6 : 1,
          transition: 'border-color 120ms, background-color 120ms',
          '&:focus-within': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }}
      >
        <input
          id={INPUT_ID}
          type="file"
          multiple
          accept={capabilities.accept}
          ref={fileInputRef}
          onChange={onFileSelect}
          disabled={uploading}
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden' }}
        />
        <CloudUploadIcon sx={{ fontSize: 40, color: dragActive ? 'primary.main' : 'text.secondary' }} />
        <Typography variant="body1" sx={{ mt: 1 }}>
          {dragActive
            ? t('documents.upload.dropHere')
            : t('documents.upload.instructionsMultiple')}
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            {t('documents.upload.supportedFormatsPrefix', {
              defaultValue: 'Unterstützte Formate: {{formats}}',
              formats: formatList(capabilities.allowed_extensions),
            })}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            {t('documents.upload.maxFileSize', {
              defaultValue: 'Maximum file size: {{size}} MB',
              size: maxFileSizeMB,
            })}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            {t('documents.upload.extraInputsHint')}
          </Typography>
        </Box>
      </Box>

      {/* Screen-reader progress announcement. */}
      <Typography component="p" role="status" aria-live="polite" sx={visuallyHidden}>
        {summary.total > 0
          ? t('documents.upload.queueProgress', { done: finished, total: summary.total })
          : ''}
      </Typography>

      {items.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', px: 1 }}>
            <Typography variant="subtitle2">
              {t('documents.upload.filesSelected', { count: summary.total })}
            </Typography>
            {(uploading || finished > 0) && (
              <Typography variant="caption" color="text.secondary">
                {t('documents.upload.queueProgress', { done: finished, total: summary.total })}
              </Typography>
            )}
          </Box>
          <List dense disablePadding sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {items.map((item) => (
              <QueueRow
                key={item.id}
                item={item}
                onRemove={onRemove}
                onTitleChange={onTitleChange}
                removable={!uploading && (item.status === 'pending' || item.status === 'error')}
                editableTitle={!uploading && items.length > 1}
              />
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

/** Memoized so it does not re-render on sibling-field changes (e.g. title typing). */
export const FileUploadTab = React.memo(FileUploadTabInner);
